import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LoopDiagram, type LoopNode } from '../LoopDiagram'
import { Kicker, Titulo, Folio } from '../type'
import { motion } from '../tokens'
import { useReducedMotion } from '../useInView'
import { trackEvent, trackMenuClick } from '../../utils/analytics'

/**
 * Easter Egg — o nome percorre o laço (D4/D36; ADR-010). Gatilho preservado: digitar `zetaz` fora de campos de texto.
 * Sete letras, sete nós: Z · e · n · t · r · i · z acendem em sequência sobre o laço-estádio do modelo do Ângelo — cada letra
 * com um significado da marca — e o último z fecha o ciclo onde o primeiro abriu: "Nada acontece fora do laço." A saída leva
 * à página /zeta (a história completa do nome e do símbolo). SVG + setTimeout: zero canvas, zero alpha sobre cor de estado.
 * Reduced-motion: todas as letras acesas de uma vez, estáticas. Esc ou clique fora fecha. trackEvent('open','EasterEgg','zetaz').
 */
const TRIGGER = 'zetaz'
const N = 7

export default function Egg() {
  const { t } = useTranslation()
  const reduced = useReducedMotion()
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0) // 0..6 letras, 7 = retorno
  const buffer = useRef('')
  const timers = useRef<number[]>([])

  const close = useCallback(() => { setOpen(false); timers.current.forEach(clearTimeout); timers.current = [] }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      if (e.key.length !== 1) return
      buffer.current = (buffer.current + e.key.toLowerCase()).slice(-TRIGGER.length)
      if (buffer.current === TRIGGER) { buffer.current = ''; setOpen(true) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    trackEvent('open', 'EasterEgg', 'zetaz')
    document.body.style.overflow = 'hidden'
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onEsc)
    if (reduced) setStep(N)
    else {
      setStep(0)
      for (let i = 1; i <= N; i++) timers.current.push(window.setTimeout(() => setStep(i), i * motion.nodeMs * 2))
    }
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onEsc); timers.current.forEach(clearTimeout); timers.current = [] }
  }, [open, reduced, close])

  if (!open) return null
  const letters = t('brand.easterEgg.letters', { returnObjects: true }) as Array<{ letter: string; kicker: string; line: string }>
  const done = step >= N
  // nós acesos até a letra atual: Construção enquanto o nome se escreve; o último z acende em Cura ao fechar
  const nodes: LoopNode[] = letters.map((_, i) => ({ t: i / N, state: i < step || done ? (i === N - 1 && done ? 'cura' : 'construcao') : 'idle', radius: i === step && !done ? 11 : undefined }))
  const current = done ? null : letters[step]
  const state = done ? 'cura' : 'construcao'
  return createPortal(
    <div className="z-egg" role="dialog" aria-modal="true" aria-label={t('brand.easterEgg.return')} data-surface="dark" data-egg-step={step} onClick={close}>
      <div className="z-egg__palco" onClick={(e) => e.stopPropagation()}>
        <div className="z-egg__laco">
          <LoopDiagram nodes={nodes} pulse={done && !reduced} pulseDuration={7} title={t('brand.easterEgg.return')} />
          <p className="z-egg__letras" aria-hidden="true">{letters.map((l, i) => <span key={i} className={i < step || done ? 'is-on' : undefined}>{l.letter}</span>)}</p>
        </div>
        <div className="z-egg__texto" data-state={state}>
          {current ? (
            <>
              <Kicker>{current.kicker} · {step + 1}/{N}</Kicker>
              <Titulo variant="manifesto" as="p">{current.letter}</Titulo>
              <p className="z-egg__linha">{current.line}</p>
            </>
          ) : (
            <>
              <Kicker>{t('brand.tagline')}</Kicker>
              <Titulo variant="editorial" as="p">{t('brand.easterEgg.return')}</Titulo>
              <p className="z-egg__linha">{t('brand.descriptor')}</p>
              <p><Link to="/brand" className="z-explorer__link" onClick={() => { trackMenuClick('/brand', 'easter-egg'); close() }}>{t('brand.easterEgg.read')}</Link></p>
            </>
          )}
          {reduced && (
            <ol className="z-egg__todas">
              {letters.map((l, i) => <li key={i} data-state={i === N - 1 ? 'cura' : 'construcao'}><span className="z-no" data-role="no" aria-hidden="true" /> <strong>{l.letter} · {l.kicker}</strong> — {l.line}</li>)}
            </ol>
          )}
        </div>
        <Folio className="z-egg__folio">{TRIGGER.toUpperCase()} · ESC</Folio>
      </div>
    </div>,
    document.body,
  )
}
